import { useMemo } from 'react'
import styled, { css } from 'styled-components'

import { useHapticFeedback } from '@blade/ui-kit'

type WalletColorSwatchProps = {
  currentColor: string
  value: string
  onChange: (value: string) => void
  size?: 's' | 'm'
}

const COLORS = [
  '#FF3535',
  '#FF5963',
  '#FF9C00',
  '#FFD21F',
  '#25BD83',
  '#256EE6',
  '#944BDD',
  '#557888',
  'var(--gray-11)',
]

export const WalletColorSwatch: React.FC<WalletColorSwatchProps> = ({
  currentColor,
  value,
  onChange,
  size = 'm',
}) => {
  const haptics = useHapticFeedback()

  const colors = useMemo(() => {
    const restColors: Array<string> = COLORS.filter((it) => it !== currentColor)

    return [currentColor, ...restColors]
  }, [currentColor])

  const renderColors = () => {
    return colors.map((color) => {
      const style = {
        '--color': color,
      } as React.CSSProperties
      const isSelected = value === color

      return (
        <ColorItem
          key={color}
          $size={size}
          $selected={isSelected}
          style={style}
          onClick={() => {
            haptics.impactOccurred('light')
            onChange(color)
          }}
        >
          <ColorSample $size={size} />
          {isSelected ? undefined : <SelectionFrame size={size} />}
        </ColorItem>
      )
    })
  }
  return <Root $size={size}>{renderColors()}</Root>
}

const Root = styled.div<{ $size: 's' | 'm' }>`
  display: grid;
  grid-auto-flow: column;
  justify-content: space-between;

  ${({ $size }) => {
    switch ($size) {
      case 's': {
        return css`
          gap: 2px;
        `
      }
      case 'm': {
        return css`
          gap: 6px;
          padding-block: 0 12px;
          padding-inline: 12px;
        `
      }
    }
  }}
`

const ColorItem = styled.div<{ $selected: boolean; $size: 's' | 'm' }>`
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;

  cursor: pointer;

  ${({ $selected }) => {
    if ($selected) {
      return css`
        background: radial-gradient(
          170% 170% at 50% -70%,
          var(--backgroundA-1) 0%,
          transparent 100%
        );
        border: thin solid var(--backgroundA-8);
      `
    }

    return css`
      border: thin solid var(--backgroundA-3);
    `
  }}

  ${({ $size }) => {
    switch ($size) {
      case 's': {
        return css`
          height: 24px;
          width: 24px;
          border-radius: 8px;
        `
      }
      case 'm': {
        return css`
          height: 40px;
          width: 40px;
          border-radius: 12px;
        `
      }
    }
  }}
`

const ColorSample = styled.div<{ $size: 's' | 'm' }>`
  background: var(--color);
  border-radius: 50%;

  ${({ $size }) => {
    switch ($size) {
      case 's': {
        return css`
          height: 14px;
          width: 14px;
        `
      }
      case 'm': {
        return css`
          height: 24px;
          width: 24px;
        `
      }
    }
  }}
`

const SelectionFrame = ({ size = 'm' }: { size?: 's' | 'm' }) => {
  const width = size === 's' ? 24 : 40
  const height = size === 's' ? 24 : 40

  return (
    <FrameSvg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect
        x="0.3"
        y="0.3"
        width="39.4"
        height="39.4"
        rx="7.7"
        stroke="url(#paint0_radial_838_10890)"
        strokeOpacity="0.2"
        strokeWidth="0.6"
      />
      <defs>
        <radialGradient
          id="paint0_radial_838_10890"
          cx="0"
          cy="0"
          r="1"
          gradientUnits="userSpaceOnUse"
          gradientTransform="translate(20 20) rotate(90) scale(29)"
        >
          <stop offset="0.75" stopColor="white" stopOpacity="0" />
          <stop offset="1" stopColor="white" />
        </radialGradient>
      </defs>
    </FrameSvg>
  )
}

const FrameSvg = styled.svg`
  display: block;
  inset: 0;
  position: absolute;
  inset: 0;
`
